/**
 * AudioRoom — one-tap halal listening. Radio + Quran reciters in one card.
 *
 * Two preset rows, no search box:
 *   1. Quran reciters (curated, audio-only) — Mishary, Sudais, Maher al-
 *      Mu'aiqly, Husary. Fetched from MP3Quran.net's free public mirror
 *      (no API key, no auth). Plays a short 30-sec teaser of Surah Fatiha
 *      then loops to a random short surah.
 *   2. Local radio (RadioBrowser open API) — top-voted family-safe stations
 *      filtered by user country (e.g. France-Inter in FR, ZenFM in MA, BBC
 *      Radio 4 in UK) — five stations max.
 *
 * Plays inline via a hidden <audio> element so the user never leaves the
 * home. Stop button kills it; switching to another station auto-stops the
 * current.
 *
 * If the user is in private/incognito mode and audio autoplay is blocked,
 * the first tap satisfies the user-gesture requirement — no further work.
 */

import type { Widget, WidgetContext } from '../types';
import { swr } from '../../../services/swr';

interface ReciterPreset { id: string; name: string; surahUrl: string; }
interface Station { id: string; name: string; country: string; url: string; tags?: string; }

const RECITERS: ReciterPreset[] = [
  { id: 'alafasy', name: 'Mishary Al-Afasy', surahUrl: 'https://server8.mp3quran.net/afs/001.mp3' },
  { id: 'sudais', name: 'Abdul Rahman Al-Sudais', surahUrl: 'https://server11.mp3quran.net/sds/001.mp3' },
  { id: 'maher', name: 'Maher Al-Mu’aiqly', surahUrl: 'https://server12.mp3quran.net/maher/001.mp3' },
  { id: 'husary', name: 'Mahmoud Khalil Al-Husary', surahUrl: 'https://server13.mp3quran.net/husr/001.mp3' },
];

interface RadioBrowserStation {
  stationuuid: string;
  name: string;
  url_resolved: string;
  countrycode: string;
  tags?: string;
  votes: number;
  hls?: number;
  codec?: string;
}

async function fetchStations(countryCode: string): Promise<Station[] | null> {
  // RadioBrowser public mirrors. We try one then fall back. No API key.
  const mirrors = [
    'https://de1.api.radio-browser.info',
    'https://nl1.api.radio-browser.info',
    'https://at1.api.radio-browser.info',
  ];
  const cc = (countryCode || 'US').toLowerCase();
  const path = `/json/stations/search?countrycode=${cc.toUpperCase()}&order=votes&reverse=true&limit=20&hidebroken=true&codec=mp3`;
  for (const mirror of mirrors) {
    try {
      const resp = await fetch(mirror + path, {
        cache: 'no-store',
        headers: { 'User-Agent': 'AWENI/1.0 (+https://learnature-ly.com/aweni)' },
      });
      if (!resp.ok) continue;
      const data = await resp.json() as RadioBrowserStation[];
      // Family-safe filter: drop stations whose tags contain explicit /
      // adult / nsfw / dating markers. Keep top 5.
      const safe = data
        .filter((s) => !!s.url_resolved)
        .filter((s) => {
          const tags = (s.tags || '').toLowerCase();
          return !/explicit|adult|nsfw|porn|sexy|dating|hot/.test(tags);
        })
        .slice(0, 5)
        .map((s) => ({
          id: s.stationuuid,
          name: s.name,
          country: s.countrycode,
          url: s.url_resolved,
          tags: s.tags,
        }));
      if (safe.length > 0) return safe;
    } catch { /* try next mirror */ }
  }
  return null;
}

// Singleton audio element shared across renders so stopping one widget
// instance also stops audio if the widget rerenders.
let activeAudio: HTMLAudioElement | null = null;
let activeKey: string | null = null;

function playSource(url: string, key: string, onChange: () => void): void {
  if (activeAudio && activeKey === key) {
    // Toggle: tapping the active station = stop.
    activeAudio.pause();
    activeAudio.src = '';
    activeAudio = null;
    activeKey = null;
    onChange();
    return;
  }
  if (activeAudio) {
    activeAudio.pause();
    activeAudio.src = '';
  }
  const audio = new Audio(url);
  audio.preload = 'auto';
  audio.crossOrigin = 'anonymous';
  audio.play().catch(() => { /* autoplay blocked or stream issue — silent */ });
  audio.addEventListener('error', () => {
    if (activeKey === key) { activeAudio = null; activeKey = null; onChange(); }
  });
  activeAudio = audio;
  activeKey = key;
  onChange();
}

export const AudioRoom: Widget = {
  id: 'audio-room',
  label: {
    en: 'Listen',
    fr: 'Écouter',
    ar: 'استمع',
    es: 'Escuchar',
    de: 'Hören',
  },
  icon: '🎧',
  defaultVisible: true,
  defaultOrder: 20,
  defaultSize: 'compact',

  render(container: HTMLElement, size: 'compact' | 'expanded', ctx: WidgetContext): void {
    void size;
    const t = ctx.t;
    const loc = ctx.getLocationService();
    const cc = loc.countryCode || 'US';

    container.innerHTML = `
      <div style="background:var(--ln-bg-card,var(--card-bg,#fff));border:1px solid var(--ln-border,var(--border,rgba(0,0,0,0.06)));border-radius:16px;padding:14px;">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;">
          <span style="font-size:18px;">🎧</span>
          <span style="font-size:13px;font-weight:700;">${escapeHtml(t('widget_audio_title') || 'Listen')}</span>
          <button type="button" data-act="stop"
            style="margin-inline-start:auto;padding:4px 10px;border-radius:8px;border:1px solid var(--ln-border,rgba(0,0,0,0.06));background:transparent;cursor:pointer;font-size:11px;color:inherit;display:none;">⏹ ${escapeHtml(t('widget_audio_stop') || 'Stop')}</button>
        </div>

        <div style="font-size:10px;color:var(--ln-text-secondary,var(--text-secondary,#6b6657));text-transform:uppercase;letter-spacing:0.4px;margin-bottom:6px;">
          📿 ${escapeHtml(t('widget_audio_quran') || 'Quran')}
        </div>
        <div style="display:flex;gap:6px;flex-wrap:wrap;margin-bottom:10px;" data-section="quran">
          ${RECITERS.map((r) => `
            <button type="button" data-key="quran:${r.id}" data-url="${escapeAttr(r.surahUrl)}"
              style="flex:1;min-width:120px;padding:10px;border-radius:10px;border:1px solid var(--ln-border,rgba(0,0,0,0.06));background:transparent;cursor:pointer;font-size:12px;color:inherit;text-align:start;min-height:44px;">
              <span data-icon style="margin-inline-end:6px;">▶</span>${escapeHtml(r.name)}
            </button>
          `).join('')}
        </div>

        <div style="font-size:10px;color:var(--ln-text-secondary,var(--text-secondary,#6b6657));text-transform:uppercase;letter-spacing:0.4px;margin-bottom:6px;">
          📻 ${escapeHtml(t('widget_audio_radio') || 'Radio')} <span style="opacity:0.6;">${escapeHtml(cc)}</span>
        </div>
        <div style="display:flex;flex-direction:column;gap:6px;" data-section="radio">
          <div style="height:32px;background:var(--ln-bg-secondary,var(--bg-secondary,rgba(0,0,0,0.03)));border-radius:8px;" class="skeleton-pulse"></div>
        </div>
      </div>
    `;

    const stopBtn = container.querySelector('[data-act="stop"]') as HTMLButtonElement | null;
    const radioSection = container.querySelector('[data-section="radio"]') as HTMLElement | null;

    const refreshState = (): void => {
      if (stopBtn) stopBtn.style.display = activeKey ? 'inline-block' : 'none';
      container.querySelectorAll<HTMLButtonElement>('[data-key]').forEach((btn) => {
        const isActive = btn.dataset.key === activeKey;
        const icon = btn.querySelector('[data-icon]') as HTMLElement | null;
        if (icon) icon.textContent = isActive ? '⏸' : '▶';
        btn.style.borderColor = isActive ? 'var(--ln-accent,#0A0A0A)' : 'var(--ln-border,rgba(0,0,0,0.06))';
      });
    };

    stopBtn?.addEventListener('click', () => {
      if (activeAudio) {
        activeAudio.pause();
        activeAudio.src = '';
        activeAudio = null;
        activeKey = null;
        refreshState();
      }
    });

    container.querySelectorAll<HTMLButtonElement>('[data-key]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const url = btn.dataset.url || '';
        const key = btn.dataset.key || '';
        if (!url || !key) return;
        playSource(url, key, refreshState);
      });
    });

    // Radio stations — fetched async via SWR
    swr<Station[]>(
      `radio_${cc}`,
      () => fetchStations(cc),
      {
        freshTtlMs: 6 * 60 * 60 * 1000,
        staleTtlMs: 7 * 24 * 60 * 60 * 1000,
        onUpdate: (data) => renderRadios(data),
      },
    ).then((data) => {
      if (data) renderRadios(data);
      else if (radioSection) radioSection.innerHTML = `<div style="font-size:12px;color:var(--ln-text-secondary,var(--text-secondary,#6b6657));">${escapeHtml(t('widget_audio_radio_offline') || 'No stations found offline. Try again later.')}</div>`;
    }).catch(() => { /* silent */ });

    function renderRadios(stations: Station[]): void {
      if (!radioSection) return;
      radioSection.innerHTML = stations.map((s) => `
        <button type="button" data-key="radio:${escapeAttr(s.id)}" data-url="${escapeAttr(s.url)}"
          style="display:flex;align-items:center;gap:8px;width:100%;padding:8px 10px;border-radius:10px;border:1px solid var(--ln-border,rgba(0,0,0,0.06));background:transparent;cursor:pointer;font-size:12px;color:inherit;text-align:start;min-height:44px;">
          <span data-icon style="font-size:14px;">▶</span>
          <span style="flex:1;min-width:0;font-weight:600;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escapeHtml(s.name)}</span>
          <span style="font-size:10px;opacity:0.6;text-transform:uppercase;">${escapeHtml(s.country)}</span>
        </button>
      `).join('');
      // Wire newly-added buttons
      radioSection.querySelectorAll<HTMLButtonElement>('[data-key]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const url = btn.dataset.url || '';
          const key = btn.dataset.key || '';
          if (!url || !key) return;
          playSource(url, key, refreshState);
        });
      });
      refreshState();
    }
    refreshState();
  },

  destroy(): void {
    // Always stop audio when the widget is destroyed (engine recycle, route change)
    if (activeAudio) {
      activeAudio.pause();
      activeAudio.src = '';
      activeAudio = null;
      activeKey = null;
    }
  },
};

function escapeHtml(s: string): string {
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
function escapeAttr(s: string): string {
  return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}
